import React from "react";
import { useStore } from "store/storeUtils";
import { useHistory, useLocation, Link } from "react-router-dom";
import { observer } from "mobx-react";
import { useTranslation } from "@tecma/i18n";
import { logger } from "@tecma/logs-fe";
import { Drawer } from "@tecma/ds/drawer";
import { Header as TecmaHeader } from "@tecma/ds/header";
import { useMutation } from "@apollo/client";
import Cookies from "js-cookie";
import Client from "gqlclient/Client";
import logo from "assets/images/tecma_logo.svg";

import Support from "components/common/Support";

import { couldOpenSupportModal } from "utils";
import { logout } from "utils/auth";
import getOrderedLanguages from "utils/getOrderedLanguages";
import { getSupportButtonConfig, SUPPORT_BUTTON_MODES } from "utils/supportButton";

import "./header.scss";

const Header = observer((props) => {
  const { setLang } = props;
  const { t } = useTranslation();
  const store = useStore();
  const history = useHistory();
  const location = useLocation();
  const [logoutMutation] = useMutation(Client.LOGOUT);
  const [isLegacySupportOpen, setIsLegacySupportOpen] = React.useState(false);

  const handleLogout = async () => {
    if (!Cookies.get("jwt")) {
      logout(history, store);
      return;
    }
    try {
      await logoutMutation();
    } catch (err) {
      logger.error(err);
    } finally {
      logout(history, store);
    }
  };
  const openResources = () => window.open(`http://resources.tecmasolutions.com/`);
  const handleChangeLang = (selectedLanguage) => {
    getOrderedLanguages(store.configLanguages, selectedLanguage);
    const trueLanguage = selectedLanguage.split("-")[0];
    const locale = selectedLanguage.split("-")[1];
    setLang(trueLanguage, locale);
  };
  const {
    shouldRender: shouldRenderSupportButton,
    targetUrl: supportButtonV2TargetUrl,
    mode: supportButtonMode,
  } = getSupportButtonConfig({
    isCustomerSupportV2Enabled: store.isCustomerSupportV2Enabled,
    area: store.area,
  });

  /**
   * Opens the V2 support destination in a new tab.
   */
  const openSupportButtonV2 = () => {
    if (!supportButtonV2TargetUrl) {
      return;
    }
    window.open(supportButtonV2TargetUrl, "_blank", "noopener,noreferrer");
  };

  /**
   * Toggles the legacy support modal from header interactions.
   *
   * @param {Event} [e] Optional click event from header items.
   */
  const toggleLegacySupport = (e) => {
    if (e && typeof e.stopPropagation === "function") {
      e.stopPropagation();
    }
    setIsLegacySupportOpen((currentValue) => !currentValue);
  };

  /**
   * Routes the support button click to the active support mode.
   *
   * @param {Event} [e] Optional click event from header items.
   */
  const handleSupportButtonClick = (e) => {
    if (supportButtonMode === SUPPORT_BUTTON_MODES.LEGACY) {
      toggleLegacySupport(e);
      return;
    }
    openSupportButtonV2();
  };

  React.useEffect(() => {
    if (supportButtonMode !== SUPPORT_BUTTON_MODES.LEGACY) {
      return;
    }
    const shouldOpenLegacySupport = couldOpenSupportModal(location.search);
    if (shouldOpenLegacySupport) {
      setIsLegacySupportOpen(true);
    }
  }, [location.search, supportButtonMode]);

  return (
    <>
      <TecmaHeader
        logo={logo}
        logoWrapper={LogoWrapper}
        desktopHeaderContent={
          <>
            {shouldRenderSupportButton && (
              <TecmaHeader.Item
                label={t("businessplatform.support.navButton")}
                iconName='headset'
                onClick={handleSupportButtonClick}
              />
            )}
            <TecmaHeader.Item
              iconName='book-open'
              rightIconName='external-link'
              label={t("businessplatform.resources")}
              onClick={openResources}
            />
            {store.loggedUser && (
              <TecmaHeader.Item label={store.loggedUserFullname} iconName='user-circle'>
                <TecmaHeader.MenuItem
                  title={store.loggedUserFullname}
                  subtitle={t(
                    `businessplatform.${store.area === "sale" ? "sell" : "rent"}.${
                      store.loggedUser.role
                    }`,
                  )}
                />
                <TecmaHeader.Divider />
                <TecmaHeader.MenuItem title={t("businessplatform.logout")} onClick={handleLogout} />
              </TecmaHeader.Item>
            )}
            <TecmaHeader.LanguageSelector
              currentLanguage={store.loggedUser?.language + "-" + store.loggedUser?.locale}
              languages={store.configLanguages}
              onChangeLanguage={handleChangeLang}
            />
          </>
        }
        mobileHeaderContent={
          shouldRenderSupportButton && (
            <TecmaHeader.Item iconName='headset' onClick={handleSupportButtonClick} />
          )
        }
        mobileDrawerContent={
          <>
            <Drawer.Content>
              {store.loggedUser && (
                <Drawer.Avatar
                  avatarProps={{
                    text: store.loggedUserFullname
                      .match(/(\b\S)?/g)
                      .join("")
                      .match(/(^\S|\S$)?/g)
                      .join("")
                      .toUpperCase(),
                  }}
                  title={store.loggedUserFullname}
                  subtitle={t(
                    `businessplatform.${store.area === "sale" ? "sell" : "rent"}.${
                      store.loggedUser.role
                    }`,
                  )}
                />
              )}

              <div>
                <Drawer.Item
                  label={t("businessplatform.resources")}
                  iconName='book-open'
                  rightIcon='external-link'
                  menuLayout='accordion'
                  onClick={openResources}
                />
                <Drawer.Item
                  label={t("businessplatform.languages")}
                  iconName='translate'
                  rightIcon='chevron-right'
                  menuLayout='page'
                >
                  <Drawer.Languages
                    currentLanguage={store.loggedUser?.language + "-" + store.loggedUser?.locale}
                    onChangeLanguage={handleChangeLang}
                    languages={store.configLanguages}
                  />
                </Drawer.Item>
              </div>
            </Drawer.Content>

            {store.loggedUser && (
              <Drawer.Footer>
                <Drawer.Item
                  label={t("businessplatform.logout")}
                  iconName='logout'
                  onClick={handleLogout}
                />
              </Drawer.Footer>
            )}
          </>
        }
      />
      <Support isOpen={isLegacySupportOpen} closeModal={toggleLegacySupport} />
    </>
  );
});

export default Header;
// eslint-disable-next-line react/prop-types
const LogoWrapper = ({ children }) => <Link to='/'>{children}</Link>;
